// "Have an invite code?" (NoCompanyView.inviteCodeCard): a code the admin shared works for any account (phone, Apple
// "Hide My Email", another address). Continue previews which company and role it leads to; joining is the separate,
// explicit "Join <company>" on the offer card.

import { CircleArrowRight, Users } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, type ChangeEvent, type RefObject } from 'react';
import type { InvitationOffer } from '@/contracts/wire';
import { Button, Field, Input, SectionCard } from '@/ui';
import { caretAfterClean, cleanCodeField, isCodeComplete } from '../inviteCode';
import { joinDetail, offerCompany } from '../offers';
import { CODE_HINT, CODE_PLACEHOLDER } from '../welcomeText';
import { ErrorNote, Footnote } from './Bits';
import { OfferSummary } from './OfferSummary';

export interface InviteCodeSectionProps {
  code: string;
  onCodeChange: (code: string) => void;
  /** The offer behind a previewed code (the field is replaced by it). */
  offer: InvitationOffer | null;
  previewing: boolean;
  joining: boolean;
  error: string | null;
  /** Something else is running: the field and buttons are off. */
  disabled: boolean;
  onPreview: () => void;
  onJoin: (offer: InvitationOffer) => void;
  /** "Use a different code": back to an empty field. */
  onClear: () => void;
  /** The code field, so the page can focus it (after "Use a different code"). */
  inputRef?: RefObject<HTMLInputElement | null>;
}

export function InviteCodeSection({
  code,
  onCodeChange,
  offer,
  previewing,
  joining,
  error,
  disabled,
  onPreview,
  onJoin,
  onClear,
  inputRef,
}: InviteCodeSectionProps) {
  const ownRef = useRef<HTMLInputElement | null>(null);
  const input = inputRef ?? ownRef;
  /** Where the caret belongs after the field rewrote what was typed. */
  const pendingCaret = useRef<number | null>(null);
  const joinButton = useRef<HTMLButtonElement | null>(null);
  const hintId = useId();
  const errorId = useId();

  /** The person submitted the code from the field (not an invite link previewed on arrival). */
  const submitted = useRef(false);

  // The previewed offer replaces the field the person was typing in: keyboard focus moves on to its Join button
  // instead of falling back to the top of the page. An offer from an invite link doesn't take focus: someone who just
  // arrived reads it first (Join is never one key press away by surprise).
  useEffect(() => {
    if (!offer || !submitted.current) return;
    submitted.current = false;
    const active = document.activeElement;
    if (active === null || active === document.body || !active.isConnected) joinButton.current?.focus();
  }, [offer]);

  useLayoutEffect(() => {
    const el = input.current;
    const caret = pendingCaret.current;
    pendingCaret.current = null;
    if (el && caret !== null && document.activeElement === el) el.setSelectionRange(caret, caret);
  }, [code, input]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    const typed = el.value;
    const cleaned = cleanCodeField(typed);
    const caret = cleaned === typed ? null : caretAfterClean(typed, el.selectionStart ?? typed.length, cleaned);
    if (cleaned === code) {
      // A rejected key: the value doesn't change, so there is no render to restore the caret after; React puts the
      // old text back now, then the caret goes back where it was.
      pendingCaret.current = null;
      if (caret !== null) requestAnimationFrame(() => {
        if (document.activeElement === el) el.setSelectionRange(caret, caret);
      });
      return;
    }
    pendingCaret.current = caret;
    onCodeChange(cleaned);
  };

  const complete = isCodeComplete(code);

  return (
    <SectionCard title="Have an invite code?" bodyClassName="flex flex-col gap-4">
      {offer ? (
        <>
          <OfferSummary offer={offer} />
          <Footnote>{joinDetail(offer)}</Footnote>
          <Button
            ref={joinButton}
            variant="primary"
            size="lg"
            fullWidth
            icon={<Users />}
            loading={joining}
            disabled={disabled}
            onClick={() => onJoin(offer)}
          >
            {joining ? 'Joining…' : `Join ${offerCompany(offer)}`}
          </Button>
          <button
            type="button"
            onClick={onClear}
            disabled={disabled}
            className="self-center rounded-control px-2 py-1 text-sm font-semibold text-link hover:underline focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-none disabled:opacity-50"
          >
            Use a different code
          </button>
        </>
      ) : (
        <form
          noValidate
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (complete && !disabled) {
              submitted.current = true;
              onPreview();
            }
          }}
        >
          <p id={hintId} className="text-sm text-fg-muted">
            {CODE_HINT}
          </p>
          <Field label="Invite code" hideLabel>
            <Input
              ref={input}
              size="lg"
              value={code}
              onChange={onChange}
              placeholder={CODE_PLACEHOLDER}
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              inputMode="text"
              disabled={disabled}
              invalid={error !== null}
              aria-describedby={error ? `${errorId} ${hintId}` : hintId}
              className="text-center font-mono text-lg font-semibold tracking-[0.12em]"
            />
          </Field>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            icon={<CircleArrowRight />}
            loading={previewing}
            disabled={!complete || disabled}
          >
            {previewing ? 'Checking code…' : 'Continue'}
          </Button>
        </form>
      )}
      {error && <ErrorNote id={errorId}>{error}</ErrorNote>}
    </SectionCard>
  );
}
